import { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { globalVariables } from "../../global/GlobalVariables";
import {
  findGetParameter,
  removeParam,
  titleCase
} from "../utilities/UtilityFunctions";
import { CssBaseline, Box, useTheme } from "@mui/material";
import "react-toastify/dist/ReactToastify.css";
import MainHeader from "./MainHeader";
import Footer from "./Footer";
import Login from "../../pages/user/Login";
import { isRouteMatch } from "../../Routes";

import {
  BCPBook,
  BCPTicket,
  BCPCertificate,
  BCPCalendarDay,
  BCPChartMixed,
  BCPShop,
  BCPUserGroup,
  BCPIdCardClip,
  BCPDiceD20,
  BCPListUl,
  BCPSearch,
  BCPGear,
  BCPRectangleHistory,
  BCPRectangleVerticalHistory,
  BCPDownToLine,
  BCPBoxOpen,
  BCPShapes,
  BCPBagShopping,
  BCPTags,
  BCPSquarePlus,
  BCPRectangleHistoryCirclePlus,
  BCPUser,
  BCPCircleStar,
  BCPDiceFive,
  BCPCommentQuestion,
  BCPArrowRightFromBracket,
  BCPPlus,
  BCPRectanglesMixed,
  BCPArrowLeftToBracket,
  BCPCodeSimple,
  BCPBank,
  BCPMoneyBills,
  BCPList,
  BCPDisplay,
  BCPLightbulb,
  BCPIdBadge
} from "../icons/bcp/BCPIcons";

import { ConfirmProvider } from "material-ui-confirm";
import ShoppingBag from "./ShoppingBag";
import DeckIcon from "../icons/DeckIcon";

import { isAppMode } from "../../global/AppOverlay";
import OAuth2IDResponse from "../../pages/user/OAuth2IDResponse/OAuth2IDResponse";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import CookieConsentForm from "./cookies/CookieConsentForm";
import { setLocalStorage } from "./cookies/CookieFunctions";
import { stringToUrl } from "../../pages/events/utils/event";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../utilities/LanguageSelector";

const COMPANY_FLAG = getEnvironmentVariable("COMPANY_FLAG");
const SUPPORTS_CARD_DATABASE =
  getEnvironmentVariable("SUPPORTS_CARD_DATABASE") === "true";
const SUPPORTS_CMS = getEnvironmentVariable("SUPPORTS_CMS") === "true";
const IS_BCP =
  getEnvironmentVariable("IS_BCP") === "true" || COMPANY_FLAG === "bcp";
const SUPPORTS_LOYALTY_STORE =
  getEnvironmentVariable("SUPPORTS_LOYALTY_STORE") === "true";
const NO_SUBSCRIPTIONS = getEnvironmentVariable("NO_SUBSCRIPTIONS") === "true";
const RANKINGS_LIST =
  getEnvironmentVariable("RANKINGS_LIST") !== undefined
    ? JSON.parse(getEnvironmentVariable("RANKINGS_LIST"))
    : undefined;
const REQUIRES_ORGANIZER =
  getEnvironmentVariable("REQUIRES_ORGANIZER") === "true";
const HIDE_RANKINGS = getEnvironmentVariable("HIDE_RANKINGS") === "true";
const IS_DEV = getEnvironmentVariable("IS_DEV") === "true";
const LEADERBOARDS =
  getEnvironmentVariable("LEADERBOARDS") === "true" ||
  getEnvironmentVariable("COMPANY_FLAG") === "gw" ||
  (getEnvironmentVariable("IS_DEV") === "true" &&
    localStorage?.getItem("leaderboardRedesign") === "true");

const MainLayout = (props) => {
  const {
    organize,
    play,
    event,
    admin,
    cms,
    storePage,
    authContext,
    itemsMiddle,
    setItemsMiddle,
    appRoute
  } = props;

  const { t } = useTranslation("translation", {
    keyPrefix: "components.MainLayout"
  });

  const globalVars = globalVariables();
  const theme = useTheme();
  var itemsTopInitial = [];

  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const [itemsTop, setItemsTop] = useState(itemsTopInitial);
  const [itemsMiddleTop, setItemsMiddleTop] = useState(null);
  const [itemsBottom, setItemsBottom] = useState([]);

  useEffect(() => {
    const check = async () => {
      try {
        var middleTopArray = null;
        var middleArray = [];
        var bottomArray = [];

        if (REQUIRES_ORGANIZER) {
          if (
            (IS_UY ||
              authContext?.authState?.identity?.isAdmin === true ||
              authContext?.authState?.identity?.isJudge ||
              authContext?.authState?.identity?.isOrganizer) &&
            itemsTop[1].title !== "Organize"
          ) {
            let topArray = [...itemsTop];
            topArray.splice(1, 0, {
              icon: BCPGear,
              href: "/organize/events",
              title: t("organize", { defaultValue: "Organize" })
            });
            setItemsTop(topArray);
          } else {
            setItemsTop(itemsTopInitial);
          }
        }

        if (organize) {
          if (
            (pathname.includes("organize/organization") ||
              organizationMatch._match) &&
            orgId
          ) {
            middleArray.push(
              {
                title: t("manageOrganization", {
                  defaultValue: "Manage Organization"
                })
              },
              {
                href: `/organize/organization/${orgId}?active_tab=overview${
                  embed ? "&embed=true" : ""
                }`,
                icon: BCPShop,
                title: t("profile", { defaultValue: "Profile" })
              },
              {
                href: `/organize/organization/${orgId}?active_tab=details${
                  embed ? "&embed=true" : ""
                }`,
                icon: BCPGear,
                title: t("settings", { defaultValue: "Settings" })
              },
              !IS_UY && {
                href: `/organize/organization/${orgId}?active_tab=banking${
                  embed ? "&embed=true" : ""
                }`,
                icon: BCPBank,
                title: t("banking", { defaultValue: "Banking" })
              },
              {
                href: `/organize/organization/${orgId}?active_tab=subscriptions${
                  embed ? "&embed=true" : ""
                }`,
                icon: BCPMoneyBills,
                title: t("subscriptions", { defaultValue: "Subscriptions" })
              },
              {
                href: `/organize/organization/${orgId}?active_tab=users${
                  embed ? "&embed=true" : ""
                }`,
                icon: BCPUserGroup,
                title: t("personnel", { defaultValue: "Personnel" })
              },
              {
                href: `/organize/organization/${orgId}?active_tab=circuits${
                  embed ? "&embed=true" : ""
                }`,
                icon: BCPCircleStar,
                title: t("circuits", { defaultValue: "Circuits" })
              },
              {
                href: `/organize/organization/${orgId}?active_tab=widgets${
                  embed ? "&embed=true" : ""
                }`,
                icon: BCPCodeSimple,
                title: t("embed", { defaultValue: "Embed" })
              },
              {
                href: `/organize/game-stores${embed ? "&embed=true" : ""}`,
                icon: BCPArrowLeftToBracket,
                title: t("viewAllOrganizations", {
                  defaultValue: "View All Organizations"
                })
              }
            );

            setItemsMiddle(middleArray);
          } else if (pathname === "organize/circuit" || circuitMatch._match) {
          } else if (eventMatch._match && pathname !== "organize/event") {
            // middleTopArray = [
            //   {
            //     href: `/organize/events?${embed ? "&embed=true" : ""}`,
            //     icon: BCPCalendarDay,
            //     title: "My Events",
            //   },
            // ];
          } else {
            if (COMPANY_FLAG !== "gw") {
              middleTopArray = [];
              middleArray.push(
                { title: t("organize", { defaultValue: "Organize" }) },
                {
                  href: `/organize/dashboard${embed ? "&embed=true" : ""}`,
                  icon: BCPRectanglesMixed,
                  title: t("dashboard", { defaultValue: "Dashboard" })
                },
                {
                  href: "/organize/events",
                  icon: BCPCalendarDay,
                  title: t("manageEvents", { defaultValue: "Manage Events" })
                },
                {
                  href: "/organize/game-stores",
                  icon: BCPShop,
                  title: t("manageOrganizations", {
                    defaultValue: "Manage Organizations"
                  })
                },
                !IS_UY
                  ? {
                      title: t("manageTeams", { defaultValue: "Manage Teams" }),
                      href: "/organize/teams",
                      icon: BCPUserGroup
                    }
                  : null,
                // TODO: Add back when circuits are implemented
                // {
                //   title: "Manage Circuits",
                //   href: "/organize/circuits",
                //   icon: BCPCircleStar,
                // },
                {
                  href: "/organize/tokens",
                  icon: BCPCertificate,
                  title: t("circuitTokens", { defaultValue: "Circuit Tokens" })
                },
                {
                  href: "/organize/ticket-sales",
                  icon: BCPTicket,
                  title: t("ticketSales", { defaultValue: "Ticket Sales" })
                }
              );

              setItemsMiddle(middleArray);
            }
          }
        } else if (admin) {
          middleArray.push({
            title: t("siteAdmin", { defaultValue: "Site Admin" })
          });

          if (authContext?.authState?.identity?.isAdmin === true) {
            middleArray.push(
              {
                href: "/admin",
                icon: BCPChartMixed,
                title: t("dashboard", { defaultValue: "Dashboard" })
              },
              {
                href: "/admin/settings?active_tab=organizations",
                icon: BCPShop,
                title: t("organizations", { defaultValue: "Organizations" })
              },
              {
                href: "/admin/settings?active_tab=event-users",
                icon: BCPUserGroup,
                title: t("eventUsers", { defaultValue: "Event Users" })
              },
              {
                href: "/admin/settings?active_tab=event-roles",
                icon: BCPIdCardClip,
                title: t("eventRoles", { defaultValue: "Event Roles" })
              }
            );
          }
          if (
            authContext?.authState?.identity?.isAdmin === true ||
            authContext?.authState?.identity?.adminGameSystems?.length > 0
          ) {
            middleArray.push({
              href: "/admin/settings?active_tab=game-systems",
              icon: BCPDiceD20,
              title: t("gameSystems", { defaultValue: "Game Systems" })
            });
          }
          if (
            getEnvironmentVariable("SUPPORTS_ARMIES") === "true" &&
            (authContext?.authState?.identity?.isAdmin === true ||
              authContext?.authState?.identity?.adminGameSystems?.length > 0)
          ) {
            middleArray.push({
              href: "/admin/settings?active_tab=armies",
              icon: BCPRectangleHistory,
              title: t("armies", { defaultValue: "Armies" })
            });
          }
          if (
            getEnvironmentVariable("SUPPORTS_SUB_FACTIONS") === "true" &&
            (authContext?.authState?.identity?.isAdmin === true ||
              authContext?.authState?.identity?.adminGameSystems?.length > 0)
          ) {
            middleArray.push({
              href: "/admin/settings?active_tab=sub-factions",
              icon: BCPRectangleVerticalHistory,
              title: t("subFactions", { defaultValue: "Sub Factions" })
            });
          }
          if (
            getEnvironmentVariable("SUPPORTS_ARMY_UNITS") === "true" &&
            (authContext?.authState?.identity?.isAdmin === true ||
              authContext?.authState?.identity?.adminGameSystems?.length > 0)
          ) {
            middleArray.push({
              href: "/admin/settings?active_tab=army-units",
              icon: BCPListUl,
              title: t("armyUnits", { defaultValue: "Army Units" })
            });
          }
          if (authContext?.authState?.identity?.isAdmin === true) {
            middleArray.push({
              href: "/admin/settings?active_tab=exports",
              icon: BCPDownToLine,
              title: t("exports", { defaultValue: "Exports" })
            });
            if (
              SUPPORTS_LOYALTY_STORE ||
              getEnvironmentVariable("SUPPORTS_PRODUCTS") === "true"
            ) {
              middleArray.push({
                title: t("storeAdmin", { defaultValue: "Store Admin" })
              });
              if (SUPPORTS_LOYALTY_STORE) {
                middleArray.push({
                  href: "/admin/shop/transactions",
                  icon: BCPBagShopping,
                  title: t("transactions", { defaultValue: "Transactions" })
                });
              }
              middleArray.push(
                {
                  href: "/admin/shop/products",
                  icon: BCPBoxOpen,
                  title: t("products", { defaultValue: "Products" })
                },
                {
                  href: "/admin/shop/categories",
                  icon: BCPShapes,
                  title: t("categories", { defaultValue: "Categories" })
                }
              );
            }
          }
          setItemsMiddle(middleArray);
        } else if (play) {
          if (COMPANY_FLAG !== "gw") {
            if (COMPANY_FLAG === "catan") {
              middleArray.push(
                {
                  href: "/play/events",
                  icon: BCPListUl,
                  title: t("eventListings", { defaultValue: "Event Listings" })
                },
                {
                  href: "/play/my-events",
                  icon: BCPCalendarDay,
                  title: t("myEvents", { defaultValue: "My Events" })
                }
              );
            } else {
              middleArray.push(
                { title: t("play", { defaultValue: "Play" }) },
                {
                  href: `/play/dashboard${embed ? "&embed=true" : ""}`,
                  icon: BCPRectanglesMixed,
                  title: t("dashboard", { defaultValue: "Dashboard" })
                },
                {
                  href: "/play/my-events",
                  icon: BCPCalendarDay,
                  title: t("myEvents", { defaultValue: "My Events" })
                },
                {
                  href: "/play/events",
                  icon: BCPSearch,
                  title: t("findEvents", { defaultValue: "Find Events" })
                },

                {
                  href: "/play/organizations",
                  icon: BCPShop,
                  title: t("findOrganizations", {
                    defaultValue: "Find Organizations"
                  })
                }
              );
              if (!IS_UY) {
                middleArray.push({
                  href: "/play/my-games",
                  icon: BCPDiceFive,
                  title: t("privateGames", { defaultValue: "Private Games" })
                });
              }
              // TODO: Add back when circuits are implemented
              // middleArray.push({
              //   href: "/play/circuits",
              //   icon: BCPCircleStar,
              //   title: "Circuits",
              // });

              middleArray.push({
                href: `/play/list-search`,
                icon: BCPList,
                title: t("armyAndDeckLists", {
                  defaultValue: "Army & Deck Lists"
                })
              });

              if (SUPPORTS_CARD_DATABASE) {
                middleArray.push(
                  { divider: true },
                  {
                    icon: DeckIcon,
                    title: t("cardSearch", { defaultValue: "Card Search" }),
                    href: "/cards"
                  },
                  {
                    icon: DeckIcon,
                    title: t("deckSearch", { defaultValue: "Deck Search" }),
                    href: "/deck-search"
                  },
                  {
                    icon: DeckIcon,
                    title: t("myDecks", { defaultValue: "My Decks" }),
                    href: "/account/decks"
                  },
                  {
                    icon: DeckIcon,
                    title: t("createNewDeck", {
                      defaultValue: "Create New Deck"
                    }),
                    href: "/deck-builder"
                  }
                );
              }
            }
          }

          setItemsMiddle(middleArray);
        } else if (cms) {
          if (
            SUPPORTS_CMS &&
            (authContext?.authState?.identity?.isEditor === true ||
              authContext?.authState?.identity?.isAdmin === true)
          ) {
            middleArray.push(
              {
                title: t("cmsDashboard", { defaultValue: "CMS Dashboard" }),
                href: "/cms/",
                icon: BCPBook
              },
              { title: t("postsAndPages", { defaultValue: "Posts & Pages" }) },
              {
                href: "/cms/my-posts",
                icon: BCPRectangleHistory,
                title: t("myPosts", { defaultValue: "My Posts" })
              },
              {
                href: "/cms/my-pages",
                icon: BCPRectangleVerticalHistory,
                title: t("myPages", { defaultValue: "My Pages" })
              },
              {
                href: "/cms/post",
                icon: BCPRectangleHistoryCirclePlus,
                title: t("createPostPage", {
                  defaultValue: "Create Post / Page"
                })
              },
              { title: t("tags", { defaultValue: "Tags" }) },
              {
                href: "/cms/tag-list",
                icon: BCPTags,
                title: t("tagList", { defaultValue: "Tag List" })
              },

              {
                href: "/cms/new-tag",
                icon: BCPSquarePlus,
                title: t("createTag", { defaultValue: "Create Tag" })
              }
            );
            setItemsMiddle(middleArray);
          }
        } else {
          setItemsMiddle(middleArray);
        }

        setItemsMiddleTop(middleTopArray);
        setItemsBottom(bottomArray);
      } catch (err) {}
    };
    check();
    // eslint-disable-next-line
  }, [authContext?.authState?.identity]);

  const embed = isAppMode() || appRoute || findGetParameter("embed") === "true";
  const exchangeTokenParam = findGetParameter("exchange_token");
  const codeParam = findGetParameter("code");

  var pathSplit = window.location.pathname.split("/");
  var basePath = "";
  if (pathSplit.length >= 3 && pathSplit[1] === "app") {
    basePath = titleCase(
      window.location.pathname.split("/").pop().split("-").join(" ")
    );
  } else {
    basePath = titleCase(
      window.location.pathname.split("/")[1].split("-").join(" ")
    );
  }

  const title =
    basePath === ""
      ? globalVars.site.title
      : `${basePath} | ${globalVars.site.title}`;

  const seo = {
    title: title,
    description: globalVars.site.description,
    image: globalVars.site.logo
  };

  var pathname = window.location.pathname.substring(1).toLowerCase();
  if (pathname[pathname.length - 1] === "/")
    pathname = pathname.substring(0, pathname.length - 1);

  var organizationMatch = isRouteMatch(
    "/organize/organization/:organizationId"
  );
  var circuitMatch = isRouteMatch("/organize/circuit/:leagueId");

  const orgId = findGetParameter("organization_id")
    ? findGetParameter("organization_id")
    : organizationMatch && pathSplit[pathSplit?.length - 1] !== "organization"
      ? pathSplit[pathSplit?.length - 1]
      : "";

  const IS_UY = getEnvironmentVariable("IS_UY") === "true";

  var showCookies =
    window.location.pathname &&
    window.location.pathname !== "/login" &&
    !embed &&
    !window.location.pathname.startsWith("/game/") &&
    authContext?.authState?.cookieConsent?.consented !== true;

  var eventMatch = isRouteMatch("/event/:eventId");
  if (!eventMatch._match) eventMatch = isRouteMatch("/organize/event/:eventId");

  // var itemsTopInitial = [];

  if (COMPANY_FLAG === "catan") {
    itemsTopInitial = [
      {
        title: t("home", { defaultValue: "Home" }),
        rel: "noopener noreferrer",
        target: "_blank",
        href: "https://catanevents.com/home"
      },
      {
        title: t("players", { defaultValue: "Players" }),
        children: [
          {
            title: t("findAnEvent", { defaultValue: "Find an Event" }),
            href: "/play/events"
          },
          {
            title: t("catanChampionship", {
              defaultValue: "CATAN Championship"
            }),
            children: [
              {
                title: t("usa", {
                  defaultValue: "United States",
                  keyPrefix: "misc.countries"
                }),
                rel: "noopener noreferrer",
                target: "_blank",
                href: "https://catanevents.com"
              },
              {
                title: t("canada", {
                  defaultValue: "Canada",
                  keyPrefix: "misc.countries"
                }),
                rel: "noopener noreferrer",
                target: "_blank",
                href: "https://catanevents.com/champ/cnc-ca/"
              },
              {
                title: t("uk", {
                  defaultValue: "United Kingdom",
                  keyPrefix: "misc.countries"
                }),
                rel: "noopener noreferrer",
                target: "_blank",
                href: "https://catanevents.com/champ/cnc-uk/"
              },
              {
                title: t("republicOfIreland", {
                  defaultValue: "Republic of Ireland",
                  keyPrefix: "misc.countries"
                }),
                rel: "noopener noreferrer",
                target: "_blank",
                href: "https://catanevents.com/champ/cnc-ire/"
              },
              {
                title: t("australia", {
                  defaultValue: "Australia",
                  keyPrefix: "misc.countries"
                }),
                rel: "noopener noreferrer",
                target: "_blank",
                href: "https://catanevents.com/champ/cnc-aus/"
              },
              {
                title: t("newZealand", {
                  defaultValue: "New Zealand",
                  keyPrefix: "misc.countries"
                }),
                rel: "noopener noreferrer",
                target: "_blank",
                href: "https://catanevents.com/champ/cnc-nz/"
              },
              {
                title: t("southAfrica", {
                  defaultValue: "South Africa",
                  keyPrefix: "misc.countries"
                }),
                rel: "noopener noreferrer",
                target: "_blank",
                href: "https://catanevents.com/champ/cnc-za/"
              },
              {
                title: t("nonEnglishCountries", {
                  defaultValue: "Non-English Countries"
                }),
                children: [
                  {
                    title: t("germany", {
                      defaultValue: "Germany",
                      keyPrefix: "misc.countries"
                    }),
                    rel: "noopener noreferrer",
                    target: "_blank",
                    href: "https://catan-meisterschaft.de/"
                  },
                  {
                    title: t("spain", {
                      defaultValue: "Spain",
                      keyPrefix: "misc.countries"
                    }),
                    rel: "noopener noreferrer",
                    target: "_blank",
                    href: "https://devir.es/agenda-clasificatorios-2023"
                  },
                  {
                    title: t("balticChampionship", {
                      defaultValue: "Baltic Championship"
                    }),
                    rel: "noopener noreferrer",
                    target: "_blank",
                    href: "https://www.brain-games.lv/blogs/viss/catan-baltic-championship-2023"
                  },
                  {
                    title: t("poland", {
                      defaultValue: "Poland",
                      keyPrefix: "misc.countries"
                    }),
                    rel: "noopener noreferrer",
                    target: "_blank",
                    href: "http://galakta.pl/mistrzostwa-polski-w-catan-2023/"
                  },
                  {
                    title: t("allOtherCountries", {
                      defaultValue: "All Other Countries"
                    }),
                    rel: "noopener noreferrer",
                    target: "_blank",
                    href: "https://www.catan.com/our-partners"
                  }
                ]
              }
            ]
          },
          {
            title: t("mastersInvitational", {
              defaultValue: "Masters Invitational"
            }),
            children: [
              {
                title: t("currentRankings", {
                  defaultValue: "Current Rankings"
                }),
                rel: "noopener noreferrer",
                target: "_blank",
                href: "https://catanevents.com/masters/masters-rankings/"
              }
            ]
          }
        ]
      },
      {
        title: t("hosts", { defaultValue: "Hosts" }),
        children: [
          {
            title: t("hostAnEvent", { defaultValue: "Host an Event" }),
            href: "/organize/events"
          },
          {
            title: t("submitEventRequest", {
              defaultValue: "Submit Event Request"
            }),
            rel: "noopener noreferrer",
            target: "_blank",
            href: "https://catanevents.com/hosts/submit-event-request/"
          },
          {
            title: t("tournamentSoftwareInstructions", {
              defaultValue: "Tournament Software Instructions"
            }),
            rel: "noopener noreferrer",
            target: "_blank",
            href: "https://catanevents.com/hosts/software-docs/"
          },
          {
            title: t("marketingAssets", { defaultValue: "Marketing Assets" }),
            rel: "noopener noreferrer",
            target: "_blank",
            href: "https://catanevents.com/hosts/marketing-assets/"
          }
        ]
      },
      {
        title: t("community", { defaultValue: "Community" }),
        children: [
          {
            title: t("catanShop", { defaultValue: "CATAN Shop" }),
            rel: "noopener noreferrer",
            target: "_blank",
            href: "https://catanshop.com/"
          },
          {
            title: t("joinDemoCrew", { defaultValue: "Join our Demo Crew" }),
            rel: "noopener noreferrer",
            target: "_blank",
            href: "https://catanevents.com/community/#Demo-Crew"
          },
          {
            title: t("support", { defaultValue: "Support" }),
            rel: "noopener noreferrer",
            target: "_blank",
            href: "https://catanevents.com/community/#Support  "
          }
        ]
      }
    ];
  } else {
    // TODO: REVISIT THIS
    if (SUPPORTS_CARD_DATABASE) {
      let cardMenu = {
        icon: DeckIcon,
        title: t("exploreDecks", { defaultValue: "Explore Decks" }),
        children: [
          {
            title: t("cardDatabase", { defaultValue: "Card Database" }),
            href: "/cards"
          },
          {
            title: t("deckDatabase", { defaultValue: "Deck Database" }),
            href: "/deck-search"
          },
          {
            title: t("myDecks", { defaultValue: "My Decks" }),
            href: "/account/decks"
          },
          {
            title: t("createNewDeck", { defaultValue: "Create New Deck" }),
            href: "/deck-builder"
          }
        ]
      };

      itemsTopInitial.push(cardMenu);
    }

    const playMenu = {
      title: t("play", { defaultValue: "Play" }),
      children: IS_UY
        ? [
            {
              title: t("myEvents", { defaultValue: "My Events" }),
              href: "/play/my-events"
            },
            {
              title: t("eventSearch", { defaultValue: "Event Search" }),
              href: "/play/events"
            },
            {
              title: t("listSearch", { defaultValue: "List Search" }),
              href: "/play/list-search"
            }
          ]
        : [
            {
              title: t("dashboard", { defaultValue: "Dashboard" }),
              href: "/play/dashboard",
              icon: BCPRectanglesMixed
            },
            { divider: true },
            {
              title: t("findEvents", { defaultValue: "Find Events" }),
              href: "/play/events",
              icon: BCPSearch
            },
            {
              title: t("myEvents", { defaultValue: "My Events" }),
              href: "/play/my-events",
              icon: BCPCalendarDay
            },
            {
              title: t("findOrganizations", {
                defaultValue: "Find Organizations"
              }),
              href: "/play/organizations",
              icon: BCPShop
            },
            {
              title: t("privateGames", { defaultValue: "Private Games" }),
              href: "/play/my-games",
              icon: BCPDiceFive
            }
          ]
    };

    if (COMPANY_FLAG === "soul-masters") {
      itemsTopInitial.push({
        title: t("deckBuilder", { defaultValue: "Deck Builder" }),
        href: "https://soulmastersdb.net/",
        target: "_blank"
      });
    }

    playMenu.children.push({
      title: t("armyAndDeckLists", { defaultValue: "Army & Deck Lists" }),
      href: "/play/list-search",
      icon: BCPListUl
    });

    const organizeMenu = {
      title: t("organize", { defaultValue: "Organize" }),
      children: [
        {
          title: t("dashboard", { defaultValue: "Dashboard" }),
          href: "/organize/dashboard",
          icon: BCPRectanglesMixed
        },
        { divider: true },

        (!REQUIRES_ORGANIZER || IS_UY) && {
          title: t("createEvent", { defaultValue: "Create Event" }),
          href: "/organize/event",
          icon: BCPPlus
        },
        !(REQUIRES_ORGANIZER || IS_UY) && {
          title: t("manageEvents", { defaultValue: "Manage Events" }),
          href: "/organize/events",
          icon: BCPCalendarDay
        },
        {
          title: t("manageOrganizations", {
            defaultValue: "Manage Organizations"
          }),
          href: "/organize/game-stores",
          icon: BCPShop
        },
        !IS_UY && {
          title: t("manageTeams", { defaultValue: "Manage Teams" }),
          href: "/organize/teams",
          icon: BCPUserGroup
        },
        {
          href: "/organize/tokens",
          icon: BCPCertificate,
          title: t("circuitTokens", { defaultValue: "Circuit Tokens" })
        },
        {
          title: t("ticketSales", { defaultValue: "Ticket Sales" }),
          href: "/organize/ticket-sales",
          icon: BCPTicket
        }
      ]
    };

    const cmsMenu = {
      title: t("news", { defaultValue: "News" }),
      children: [
        {
          title: t("news", { defaultValue: "News" }),
          href: "/news",
          icon: BCPBook
        }
      ]
    };
    if (
      authContext?.authState?.identity?.isAdmin === true ||
      authContext?.authState?.identity?.isEditor === true
    ) {
      cmsMenu.children.push(
        { divider: true },
        {
          title: t("cmsDashboard", { defaultValue: "CMS Dashboard" }),
          href: "/cms/",
          icon: BCPBook
        },
        {
          title: t("myPages", { defaultValue: "My Pages" }),
          href: "/cms/my-pages",
          icon: BCPRectangleVerticalHistory
        },
        {
          title: t("createPostPage", { defaultValue: "Create Post / Page" }),
          href: "/cms/post",
          icon: BCPRectangleHistoryCirclePlus
        },
        {
          title: t("createTag", { defaultValue: "Create Tag" }),
          href: "/cms/new-tag",
          icon: BCPPlus
        },
        {
          title: t("tagList", { defaultValue: "Tag List" }),
          href: "/cms/tag-list",
          icon: BCPTags
        }
      );
    }

    const accountMenu = {
      title: t("account", { defaultValue: "Account" }),
      children: authContext?.authState?.isAuthenticated
        ? [
            {
              title: t("myAccount", { defaultValue: "My Account" }),
              href: "/account",
              icon: BCPUser
            },
            IS_BCP || !NO_SUBSCRIPTIONS
              ? {
                  title: t("subscriptions", { defaultValue: "Subscriptions" }),
                  href: "/account?active_tab=subscriptions",
                  icon: BCPMoneyBills
                }
              : null,
            {
              title: t("display", { defaultValue: "Display" }),
              href: "/account?active_tab=display",
              icon: BCPDisplay
            },

            {
              title: t("help", { defaultValue: "Help" }),
              href: "/support",
              icon: BCPCommentQuestion
            },
            {
              title: t("signOut", { defaultValue: "Sign Out" }),
              href: "/logout",
              icon: BCPArrowRightFromBracket
            }
          ]
        : [
            {
              title: t("signInSignUp", { defaultValue: "Sign In / Sign Up" }),
              href: `/login?redirect_url=${stringToUrl(window.location.href)}`,
              icon: BCPUser
            }
          ]
    };
    // TODO: Add back when pages are created
    // const moreMenu = {
    //   title: "More",
    //   children: [
    //     {
    //       title: t("glossary", { defaultValue: "Glossary" }),
    //       href: "/glossary",
    //       icon: BCPBookOpen,
    //     },
    //     {
    //       title: t("partnerships", { defaultValue: "Partnerships" }),
    //       href: "/partnerships",
    //       icon: BCPHandshake,
    //     },
    //     {
    //       title: t("aboutUs", { defaultValue: "About Us" }),
    //       href: "/support",
    //       icon: BCPUserGroup,
    //     },
    //     {
    //       title: t("contact", { defaultValue: "Contact" }),
    //       href: "/support",
    //       icon: BCPEnvelope,
    //     },
    //     {
    //       title: t("downloadApp", { defaultValue: "Download App" }),
    //       href: "/download-app",
    //       icon: BCPDownToLine,
    //     },
    //   ],
    // };
    // TODO: Add admin menu pages, remove from Account menu
    const adminMenu = {
      title: t("admin", { defaultValue: "Admin" }),
      children: [
        {
          title: t("dashboard", { defaultValue: "Dashboard" }),
          href: "/admin",
          icon: BCPRectanglesMixed
        },
        { divider: true },

        {
          title: t("organizations", { defaultValue: "Organizations" }),
          href: "/admin/settings?active_tab=organizations",
          icon: BCPShop
        },
        {
          title: t("eventUsers", { defaultValue: "Event Users" }),
          href: "/admin/settings?active_tab=event-users",
          icon: BCPUserGroup
        },
        {
          title: t("eventRoles", { defaultValue: "Event Roles" }),
          href: "/admin/settings?active_tab=event-roles",
          icon: BCPIdBadge
        },
        {
          title: t("gameSystems", { defaultValue: "Game Systems" }),
          href: "/admin/settings?active_tab=game-systems",
          icon: BCPDiceD20
        },

        getEnvironmentVariable("SUPPORTS_ARMIES") === "true"
          ? {
              title: t("armies", { defaultValue: "Armies" }),
              href: "/admin/settings?active_tab=armies",
              icon: BCPRectangleHistory
            }
          : null,
        getEnvironmentVariable("SUPPORTS_SUB_FACTIONS") === "true"
          ? {
              title: t("subFactions", { defaultValue: "Sub Factions" }),
              href: "/admin/settings?active_tab=sub-factions",
              icon: BCPRectangleVerticalHistory
            }
          : null,
        getEnvironmentVariable("SUPPORTS_ARMY_UNITS") === "true"
          ? {
              title: t("armyUnits", { defaultValue: "Army Units" }),
              href: "/admin/settings?active_tab=army-units",
              icon: BCPListUl
            }
          : null,
        {
          title: t("exports", { defaultValue: "Exports" }),
          href: "/admin/settings?active_tab=exports",
          icon: BCPDownToLine
        },

        {
          title: t("utilities", { defaultValue: "Utilities" }),
          href: "/admin/utilities",
          icon: BCPLightbulb
        },
        getEnvironmentVariable("SUPPORTS_LOYALTY_STORE") === "true" &&
        getEnvironmentVariable("SUPPORTS_PRODUCTS") === "true"
          ? {
              title: t("store", { defaultValue: "Store" }),
              href: "/admin/shop",
              icon: BCPBagShopping
            }
          : null
      ]
    };

    itemsTopInitial.push(playMenu);
    itemsTopInitial.push(organizeMenu);
    if (SUPPORTS_CMS) {
      itemsTopInitial.push(cmsMenu);
    }
    if (!HIDE_RANKINGS) {
      let rankingsMenu = {
        title: LEADERBOARDS
          ? t("leaderboards", { defaultValue: "Leaderboards" })
          : t("rankings", { defaultValue: "Rankings" }),
        children: []
      };

      if (
        RANKINGS_LIST !== undefined &&
        Array.isArray(RANKINGS_LIST) &&
        RANKINGS_LIST?.length > 0
      ) {
        rankingsMenu = {
          title: LEADERBOARDS
            ? t("leaderboards", { defaultValue: "Leaderboards" })
            : t("rankings", { defaultValue: "Rankings" }),
          children: RANKINGS_LIST
        };
      } else {
        if (IS_BCP) {
          rankingsMenu.children.push({
            title: t("bcpRankings", { defaultValue: "BCP Rankings" }),
            href: LEADERBOARDS ? "/leaderboards" : "/rankings",
            icon: BCPChartMixed
          });

          if (!IS_DEV) {
            rankingsMenu.children.unshift({
              title: t("itcRankings", { defaultValue: "ITC Rankings" }),
              href: `/organization/${LEADERBOARDS ? "leaderboards" : "rankings"}/OlkMrRfxrN`,
              icon: BCPChartMixed
            });
          }
        }
      }

      if (rankingsMenu?.children?.length > 0) {
        itemsTopInitial.push(rankingsMenu);
      }
    }

    itemsTopInitial.push({
      title: t("subscribe", { defaultValue: "Subscribe" }),
      href: "/subscription"
    });

    const languageSelector = LanguageSelector({
      type: "mainLayout",
      authContext
    });
    if (languageSelector) {
      itemsTopInitial.push(languageSelector);
    }

    if (SUPPORTS_LOYALTY_STORE) {
      itemsTopInitial.push({
        href: "/shop",
        title: t("shop", { defaultValue: "Shop" })
      });
    }
    itemsTopInitial.push(accountMenu);

    if (
      authContext?.authState?.identity?.isAdmin === true ||
      authContext?.authState?.identity?.adminGameSystems?.length > 0
    ) {
      itemsTopInitial.push(adminMenu);
    }
    // itemsTopInitial.push(moreMenu);
  }

  // Loops through itemsTopInitial and removes any null children
  itemsTopInitial.forEach((item) => {
    if (item.children) {
      item.children = item.children.filter((child) => child !== null);
    }
  });

  if (!itemsTop?.length) {
    setItemsTop(itemsTopInitial);
  }

  if (
    (exchangeTokenParam || codeParam) &&
    window.location.pathname !== "/login" &&
    !authContext?.authState?.identity
  ) {
    const redirectUrl = removeParam(
      `${exchangeTokenParam ? "exchange_token" : "code"}`,
      window.location.href
    );

    setLocalStorage("bcpRedirectUrl", redirectUrl);

    return <OAuth2IDResponse redirectUrl={redirectUrl} />;
  }

  return (
    <>
      <Helmet
        title={seo.title}
        description={seo.description}
        meta={[
          {
            name: "description",
            property: "og:description",
            content: seo.description
          },
          { property: "og:title", content: seo.title },
          { property: "og:image", content: seo.image },
          { property: "og:image:type", content: "image/jpeg" },
          { property: "twitter:image:src", content: seo.image },
          { property: "twitter:title", content: seo.title },
          { property: "twitter:description", content: seo.description }
        ]}
      />
      <Box>
        <CssBaseline />
        {!embed && (
          <MainHeader
            drawerOpen={isMobileNavOpen}
            setDrawerOpen={setMobileNavOpen}
            event={event}
            authContext={authContext}
            itemsTop={itemsTop}
            itemsMiddleTop={itemsMiddleTop}
            itemsMiddle={itemsMiddle}
            itemsBottom={itemsBottom}
          />
        )}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
        />
        <Box
          sx={
            !embed
              ? {
                  paddingTop: [
                    "56px",
                    "64px",
                    globalVariables().menuArrayTop?.length ? "116px" : "64px"
                  ],
                  minHeight: "calc(100vh - 146px)",
                  paddingLeft: [
                    0,
                    0,
                    0,
                    Boolean(itemsMiddle && itemsMiddle.length > 0) ? "256px" : 0
                  ]
                }
              : {}
          }
        >
          <ConfirmProvider
            useLegacyReturn
            defaultOptions={{
              confirmationButtonProps: {
                variant: "outlined",
                color: "primary"
              },
              cancellationButtonProps: {
                variant: "outlined",
                color: "warning"
              },
              contentProps: {
                sx:
                  theme?.palette?.mode === "dark"
                    ? {
                        "& .MuiDialogContentText-root": {
                          color: "text.primary"
                        }
                      }
                    : null
              }
            }}
          >
            {authContext?.authState?.isLogin && <Login />}
            {!authContext?.authState?.isLogin && <Outlet />}
          </ConfirmProvider>

          {showCookies && authContext?.authState && (
            <CookieConsentForm authContext={authContext} />
          )}
        </Box>
        {storePage && <ShoppingBag />}
        {!embed && (
          <Footer
            paddingLeft={[
              2,
              2,
              2,
              Boolean(itemsMiddle && itemsMiddle.length > 0) ? "256px" : 0
            ]}
          />
        )}
      </Box>
    </>
  );
};

export default MainLayout;
